import React, { useState, useEffect, useRef } from 'react';

// Custom hook for count-up animation with easing
const useCountAnimation = (end, duration = 2000, easing = 'ease-out-crust') => {
  const [count, setCount] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const elementRef = useRef(null);
  
  // Easing functions
  const easingFunctions = {
    'ease-out-crust': (t) => 1 - Math.pow(1 - t, 3),
    'ease-spring': (t) => 1 + Math.pow(2, -10 * t) * Math.sin((t - 0.1) * 5 * Math.PI),
    'ease-bounce': (t) => {
      if (t < 1/2.75) return 7.5625 * t * t;
      if (t < 2/2.75) return 7.5625 * (t -= 1.5/2.75) * t + 0.75;
      if (t < 2.5/2.75) return 7.5625 * (t -= 2.25/2.75) * t + 0.9375;
      return 7.5625 * (t -= 2.625/2.75) * t + 0.984375;
    }
  };
  
  // Intersection Observer for triggering animation
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !isVisible) {
          setIsVisible(true);
        }
      },
      { threshold: 0.3 }
    );
    
    if (elementRef.current) {
      observer.observe(elementRef.current);
    }
    
    return () => observer.disconnect();
  }, [isVisible]);
  
  // Count animation
  useEffect(() => {
    if (!isVisible) return;
    
    let startTime = null;
    const easeFn = easingFunctions[easing] || easingFunctions['ease-out-crust'];
    
    const animate = (currentTime) => {
      if (!startTime) startTime = currentTime;
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      
      const easedProgress = easeFn(progress);
      const currentCount = Math.floor(easedProgress * end);
      
      setCount(currentCount);
      
      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        setCount(end); // Ensure we end exactly at the target
      }
    };
    
    requestAnimationFrame(animate);
  }, [isVisible, end, duration, easing]);
  
  return { count, elementRef, isVisible };
};

// Number formatter for different types
const formatNumber = (num, type = 'default', locale = 'fr-FR') => {
  const formatters = {
    default: (n) => n.toLocaleString(locale),
    currency: (n) => new Intl.NumberFormat(locale, { 
      style: 'currency', 
      currency: 'EUR',
      minimumFractionDigits: 0 
    }).format(n),
    percentage: (n) => `${n}%`,
    compact: (n) => {
      if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
      if (n >= 1000) return `${(n / 1000).toFixed(0)}K`;
      return n.toString();
    }
  };
  
  return formatters[type] ? formatters[type](num) : formatters.default(num);
};

// Main AnimatedCounter Component
export function AnimatedCounter({ 
  end, 
  duration = 2000,
  easing = 'ease-out-crust',
  format = 'default',
  prefix = '',
  suffix = '',
  className = '',
  delay = 0,
  size = 'large',
  style = {},
  ...props 
}) {
  const { count, elementRef, isVisible } = useCountAnimation(end, duration, easing);
  
  // Size-based styling
  const sizeClasses = {
    small: 'text-2xl md:text-3xl',
    medium: 'text-3xl md:text-4xl',
    large: 'text-4xl md:text-6xl',
    xl: 'text-5xl md:text-7xl'
  };
  
  return (
    <div 
      ref={elementRef}
      className={`
        font-heading font-bold text-primary
        ${sizeClasses[size]}
        ${className}
      `}
      style={{ 
        animationDelay: `${delay}ms`,
        ...style 
      }}
      {...props}
    >
      <span className="inline-block transition-all duration-300">
        {prefix}
        <span className={`
          inline-block tabular-nums
          ${isVisible ? 'animate-pulse-gold' : ''}
        `}>
          {formatNumber(count, format)}
        </span>
        {suffix}
      </span>
    </div>
  );
}

// Statistics Block Component for Hero Section
export function StatsBlock({ stats = [], className = '', ...props }) {
  return (
    <div 
      className={`
        grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-12
        ${className}
      `}
      {...props}
    >
      {stats.map((stat, index) => (
        <div 
          key={stat.label || index}
          className="text-center group"
        >
          <AnimatedCounter
            end={stat.value}
            format={stat.format || 'default'}
            suffix={stat.suffix || ''}
            prefix={stat.prefix || ''}
            delay={index * 200}
            size={stat.size || 'large'}
            className="block mb-2 group-hover:text-accent transition-colors duration-300"
          />
          
          <p className="text-sm md:text-base text-text-secondary font-body uppercase tracking-wider">
            {stat.label}
          </p>
          
          {stat.description && (
            <p className="text-xs text-text-muted mt-1 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
              {stat.description}
            </p>
          )}
        </div>
      ))}
    </div>
  );
}

// Floating Counter Badge (for notifications, etc.)
export function CounterBadge({ 
  count, 
  maxCount = 99, 
  showZero = false,
  className = '',
  ...props 
}) {
  const displayCount = count > maxCount ? `${maxCount}+` : count;
  
  if (!showZero && count === 0) {
    return null;
  }
  
  return (
    <span 
      className={`
        inline-flex items-center justify-center
        min-w-[20px] h-5 px-1
        text-xs font-semibold text-white
        bg-accent rounded-full
        animate-in fade-in-50 zoom-in-75 duration-300
        ${className}
      `}
      {...props}
    >
      {displayCount}
    </span>
  );
}

export default AnimatedCounter;
